<template>
  <!-- Teleported to <body>: `position: fixed` resolves against the nearest
       transformed ancestor, and this site's pages carry a permanently
       translated track. -->
  <Teleport to="body">
    <Transition name="locked-fade">
      <div v-if="item" class="locked-overlay" @click="$emit('close')">
        <div
          class="locked"
          role="dialog"
          aria-modal="true"
          :aria-label="heading"
          @click.stop
        >
          <button type="button" class="locked__close" aria-label="Close" @click="$emit('close')">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div class="locked__mark">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 21s-7-4.4-9.3-8.6A5.3 5.3 0 0 1 12 6.2a5.3 5.3 0 0 1 9.3 6.2C19 16.6 12 21 12 21z" />
            </svg>
          </div>

          <h3 class="locked__title">{{ heading }}</h3>

          <p class="locked__body">
            <template v-if="locked">
              {{ item.kind }} are not available at the moment. Follow along on
              either account below to see new work as it lands, and to catch the
              library opening again.
            </template>
            <template v-else>
              {{ item.kind }} live behind a follow. Give me one on either account and
              the library is yours, along with everything that lands there next.
            </template>
          </p>

          <div class="locked__accounts">
            <!-- Real links in both states. Somebody who came for the library
                 is exactly the person worth pointing at the accounts, and the
                 follow is worth having whether or not the door is open.
                 Rendered as inert buttons while locked, the invitation above
                 was one the page then refused to honour.

                 Nothing is bypassed by this: following has never been what
                 opens the library. Only `proceed` does that, its button is not
                 rendered while locked, and it is guarded besides. -->
            <a
              v-for="account in accounts"
              :key="account.name"
              :href="account.url"
              target="_blank"
              rel="noopener noreferrer"
              class="locked__account"
              @click="touched = true"
            >
              <svg v-if="account.icon === 'instagram'" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <line x1="17.5" y1="6.5" x2="17.5" y2="6.5" />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="17" height="17" fill="currentColor" stroke="none">
                <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24H16.2l-5.21-6.82-5.97 6.82H1.7l7.73-8.83L1.28 2.25h6.93l4.71 6.23zm-1.16 17.52h1.83L7.08 4.13H5.11z" />
              </svg>
              <span>{{ account.name }}</span>
            </a>
          </div>

          <p v-if="locked && notified" class="locked__notice" role="status">
            Access is not available at the moment. Please check back later.
          </p>

          <button v-if="!locked" type="button" class="locked__continue" @click="proceed">
            {{ touched ? 'Done, take me in' : 'I already follow' }}
          </button>
          <button v-else type="button" class="locked__continue" @click="$emit('close')">
            Close
          </button>

          <p class="locked__small">
            {{ locked
              ? 'The library will be back. New work reaches the accounts first.'
              : 'Asked once. After that the library stays open on this device.' }}
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref } from 'vue';
import { personalInfo } from '~/data/content';

const props = defineProps({
  // { kind, href } while open, null when closed.
  item: { type: Object, default: null },
  // Switched off in the admin. The gate then explains rather than opening,
  // even for someone who followed months ago.
  locked: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const heading = computed(() => (props.locked ? 'Not available right now' : 'Follow to unlock'));
const touched = ref(false);
const notified = ref(false);

const router = useRouter();
const { unlock } = useFollowGate();

// Only accounts that actually have a link. An Instagram entry with no URL is
// waiting on a handle, and offering it would send people nowhere.
const accounts = computed(() =>
  personalInfo.socialLinks.filter(
    (link) => link.url && ['Instagram', 'X'].includes(link.name)
  )
);

function proceed() {
  // Guarded as well as hidden. The button is not rendered while locked, but
  // this is the only thing standing between a click and the library.
  if (props.locked) {
    notified.value = true;
    return;
  }
  unlock();
  emit('close');
  router.push(props.item?.href ?? '/library');
}
</script>

<style lang="scss" scoped>
.locked-overlay {
  position: fixed;
  inset: 0;
  z-index: 3200;
  display: flex;
  justify-content: center;
  // The card is centred by its own auto margin rather than by `align-items`,
  // which overflows past the top edge when the card is taller than the screen
  // and puts the close button somewhere nothing can scroll to.
  overflow-y: auto;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.5);
}

.locked {
  position: relative;
  margin: auto;
  width: 100%;
  max-width: 390px;
  padding: 2rem 1.75rem 1.5rem;
  border-radius: 18px;
  border: 1px solid rgba(var(--border-rgb), 0.4);
  background: var(--background);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.locked__close {
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: rgba(var(--muted-rgb), 0.12);
  color: var(--text);
  cursor: pointer;

  &:hover { background: rgba(var(--muted-rgb), 0.22); }
}

.locked__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: 0 auto 0.9rem;
  border-radius: 50%;
  background: rgba(var(--accent-rgb), 0.1);
  color: var(--accent);
}

.locked__title {
  margin: 0 0 0.55rem;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
}

.locked__body {
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.65;
  color: var(--secondary);
}

.locked__accounts {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.25rem;
}

.locked__notice {
  margin: 0.9rem 0 0;
  padding: 0.55rem 0.7rem;
  border-radius: 10px;
  background: rgba(var(--muted-rgb), 0.12);
  font-size: 0.78rem;
  line-height: 1.5;
  color: var(--secondary);
}

.locked__account {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  border: 1px solid rgba(var(--accent-rgb), 0.45);
  background: rgba(var(--accent-rgb), 0.08);
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--accent);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: rgba(var(--accent-rgb), 0.16);
    text-decoration: none;
  }
}

.locked__continue {
  width: 100%;
  margin-top: 0.75rem;
  padding: 0.55rem 1.1rem;
  border-radius: 999px;
  border: none;
  background: rgba(var(--muted-rgb), 0.12);
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover { background: rgba(var(--muted-rgb), 0.2); }
}

.locked__small {
  margin: 0.7rem 0 0;
  font-size: 0.7rem;
  color: var(--muted);
}

.locked-fade-enter-active,
.locked-fade-leave-active {
  transition: opacity 0.22s ease;

  .locked { transition: transform 0.22s ease; }
}

.locked-fade-enter-from,
.locked-fade-leave-to {
  opacity: 0;

  .locked { transform: translateY(8px) scale(0.97); }
}
</style>
